"use client";

import { useLocations } from "@/hooks/use-locations";
import { useDashboards } from "@/hooks/use-dashboards";
import { useDelegation } from "@/hooks/use-delegation";
import {
  createNavigationLevel,
  findMenuItemById,
  getMainMenu,
  getMenuForNavigationLevel,
  getNavigationLevelFromPath,
  getUserRole,
} from "@/lib/shared/menu-utils";
import { useAuthStore } from "@/stores/auth-store";
import { NavigationItem, NavigationLevel, UserRole } from "@/types/menu";
import { usePathname, useRouter } from "next/navigation";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface NavigationContextType {
  currentLevel: NavigationLevel | null;
  navigationStack: NavigationLevel[];
  navigationItems: NavigationItem[];
  userRole: UserRole | null;
  isLoading: boolean;
  isDashboardsLoading: boolean;
  setNavigationLevel: (level: NavigationLevel) => void;
  navigateBack: () => void;
  navigateToLocation: (
    locationId: string,
    locationName: string
  ) => void;
  navigateToLocationSection: (
    locationId: string,
    locationName: string,
    section: string
  ) => void;
  resetToMain: () => void;
  isNavigationActive: (path: string) => boolean;
  refreshNavigation: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined
);

export function NavigationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [currentLevel, setCurrentLevel] = useState<NavigationLevel | null>(
    null
  );
  const [navigationStack, setNavigationStack] = useState<NavigationLevel[]>([]);
  const [navigationItems, setNavigationItems] = useState<NavigationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuthStore();
  const { data: locationsData } = useLocations();
  const { data: dashboardsData, isLoading: isDashboardsLoading } = useDashboards();
  const delegationContext = useDelegation();

  // Memoize locations and dashboards to prevent unnecessary re-renders
  // Include delegation context to ensure locations update when delegation changes
  const locations = useMemo(
    () => locationsData?.locations || [],
    [locationsData?.locations, delegationContext.isDelegating, delegationContext.currentDelegation?.entityId]
  );

  const dashboards = useMemo(
    () => dashboardsData || [],
    [dashboardsData, delegationContext.isDelegating, delegationContext.currentDelegation?.entityId]
  );

  // Get user role with delegation context
  const userRole = useMemo(() => getUserRole(user?.userInfo, delegationContext), [user?.userInfo, delegationContext]);

  // Define resetToMain first
  const resetToMain = useMemo(() => {
    return () => {
      setCurrentLevel(null);
      setNavigationStack([]);
    };
  }, []);

  // Define buildNavigationStack
  const buildNavigationStack = useMemo(() => {
    return (level: NavigationLevel) => {
      const stack: NavigationLevel[] = [];

      // Build stack based on level type and data
      if (
        level.type === "locations" ||
        level.type === "devices" ||
        level.type === "organizations" ||
        level.type === "administration"
      ) {
        stack.push(level);
      } else if (level.type === "location") {
        // Location level needs locations parent
        const locationsLevel = createNavigationLevel("locations");
        if (locationsLevel) stack.push(locationsLevel);
        stack.push(level);
      } else if (level.type === "location-section") {
        // Location section needs both locations and location parents
        const locationsLevel = createNavigationLevel("locations");
        if (locationsLevel) stack.push(locationsLevel);

        const locationLevel = createNavigationLevel(
          "location",
          level.data
        );
        if (locationLevel) stack.push(locationLevel);

        stack.push(level);
      }

      setNavigationStack(stack);
    };
  }, []);

  // Define updateNavigationItems
  const updateNavigationItems = useMemo(() => {
    return (level: NavigationLevel | null) => {
      setIsLoading(true);
      try {
        if (!level || level.type === "main") {
          // Main navigation - get from config with cached locations and dashboards
          const mainItems = getMainMenu(userRole, locations, dashboards);
          setNavigationItems(mainItems);
        } else {
          // Get menu items for this navigation level type
          const variables = level.data || {};
          const items = getMenuForNavigationLevel(
            level.type,
            userRole,
            locations,
            dashboards,
            variables
          );
          setNavigationItems(items);
        }
      } finally {
        setIsLoading(false);
      }
    };
  }, [userRole, locations, dashboards]);

  // Initialize navigation level based on current path - purely config-driven
  useEffect(() => {
    const level = getNavigationLevelFromPath(pathname);
    if (level) {
      // Resolve location names from cached data
      if (level.data?.locationId && locations.length > 0) {
        const location = locations.find(
          (loc) => loc.id === level.data.locationId
        );
        if (location) {
          level.data.locationName = location.name;
          level.title =
            level.type === "location" ? location.name : level.title;
        }
      }
      setCurrentLevel(level);
      buildNavigationStack(level);
    } else {
      resetToMain();
    }
  }, [pathname, locations, buildNavigationStack, resetToMain]);

  // Update navigation items when level, user role, or installations change - purely config-driven
  useEffect(() => {
    updateNavigationItems(currentLevel);
  }, [currentLevel, updateNavigationItems]);

  // Force navigation refresh when delegation context changes
  useEffect(() => {
    // When delegation context changes, force a navigation refresh to clear old data
    if (delegationContext.isDelegating !== undefined) { // Only after initial load
      updateNavigationItems(currentLevel);
    }
  }, [delegationContext.isDelegating, delegationContext.currentDelegation?.entityId, currentLevel, updateNavigationItems]);

  const setNavigationLevel = useMemo(() => {
    return (level: NavigationLevel) => {
      setCurrentLevel(level);
      buildNavigationStack(level);
    };
  }, [buildNavigationStack]);

  const navigateBack = useMemo(() => {
    return () => {
      if (navigationStack.length > 1) {
        const newStack = navigationStack.slice(0, -1);
        const previousLevel = newStack[newStack.length - 1];
        setNavigationStack(newStack);
        setCurrentLevel(previousLevel);

        if (previousLevel.path) {
          router.push(previousLevel.path);
        }
      } else {
        resetToMain();
        router.push("/dash");
      }
    };
  }, [navigationStack, router, resetToMain]);

  const navigateToLocation = useMemo(() => {
    return (locationId: string, locationName: string) => {
      const level = createNavigationLevel("location", {
        locationId,
        locationName,
      });
      if (level) {
        setNavigationLevel(level);
      }
    };
  }, [setNavigationLevel]);

  const navigateToLocationSection = useMemo(() => {
    return (
      locationId: string,
      locationName: string,
      section: string
    ) => {
      const level = createNavigationLevel("location-section", {
        locationId,
        locationName,
        section,
      });
      if (level) {
        setNavigationLevel(level);
      }
    };
  }, [setNavigationLevel]);

  const isNavigationActive = useMemo(() => {
    return (path: string) => {
      return pathname === path || pathname.startsWith(path + "/");
    };
  }, [pathname]);

  const refreshNavigation = useMemo(() => {
    return () => {
      updateNavigationItems(currentLevel);
    };
  }, [currentLevel, updateNavigationItems]);

  const contextValue: NavigationContextType = useMemo(
    () => ({
      currentLevel,
      navigationStack,
      navigationItems,
      userRole,
      isLoading,
      isDashboardsLoading,
      setNavigationLevel,
      navigateBack,
      navigateToLocation,
      navigateToLocationSection,
      resetToMain,
      isNavigationActive,
      refreshNavigation,
    }),
    [
      currentLevel,
      navigationStack,
      navigationItems,
      userRole,
      isLoading,
      isDashboardsLoading,
      setNavigationLevel,
      navigateBack,
      navigateToLocation,
      navigateToLocationSection,
      resetToMain,
      isNavigationActive,
      refreshNavigation,
    ]
  );

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
}

// Re-export types for backward compatibility
export type { NavigationItem, NavigationLevel };
